/*
 * Deferred collection carousels.
 *
 * Each [data-deferred-collection] placeholder names a section in the mirror
 * template. When the placeholder gets within rootMargin of the viewport we
 * pull that section through the Section Rendering API and swap it in.
 *
 * innerHTML does not execute <script> nodes, and section-collection.liquid
 * ships inline script that wires up Flickity, so scripts are re-created
 * explicitly after injection. Custom elements (<grid-slider>, <hover-button>,
 * <text-highlight>) upgrade on their own once connected.
 *
 * Scroll reveal has to be triggered by hand. theme.js wires its [data-aos]
 * elements to an IntersectionObserver on their [data-aos-anchor] at load, and
 * its MutationObserver only re-runs that for a handful of known containers
 * (cart items, collection filters, Ajaxinate) -- a swapped-in section is not
 * one of them. Without the class the section keeps the stylesheet default of
 * opacity 0.001 and stays invisible even though it is in the DOM.
 */
(function () {
  'use strict';

  if (window.__deferredCollectionsInit) return;
  window.__deferredCollectionsInit = true;

  var ROOT_MARGIN = '900px 0px';

  function reviveScripts(root) {
    var scripts = root.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');
      for (var j = 0; j < old.attributes.length; j++) {
        fresh.setAttribute(old.attributes[j].name, old.attributes[j].value);
      }
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    }
  }

  // Mirror of theme.js's reveal step for content it never got to observe.
  // Injection only happens within ROOT_MARGIN of the viewport, so revealing
  // straight away lands at the same moment the observer would have fired.
  function revealAnimations(root) {
    var hidden = root.querySelectorAll('[data-aos]:not(.aos-animate)');
    requestAnimationFrame(function () {
      for (var i = 0; i < hidden.length; i++) hidden[i].classList.add('aos-animate');
    });

    var custom = root.querySelectorAll('text-highlight, text-count-up');
    for (var j = 0; j < custom.length; j++) {
      if (typeof custom[j].shouldAnimate === 'function') custom[j].shouldAnimate();
    }
  }

  function load(placeholder) {
    var target = placeholder.getAttribute('data-target-section');
    var path = placeholder.getAttribute('data-source-path');
    if (!target || !path) return Promise.resolve();

    var sep = path.indexOf('?') === -1 ? '?' : '&';
    var url = path + sep + 'section_id=' + encodeURIComponent(target);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
        return res.text();
      })
      .then(function (html) {
        if (!html || !html.trim()) throw new Error('empty section for ' + target);
        var holder = document.createElement('div');
        holder.innerHTML = html.trim();
        var node = holder.firstElementChild;
        if (!node) throw new Error('unparseable section for ' + target);
        placeholder.parentNode.replaceChild(node, placeholder);
        reviveScripts(node);
        revealAnimations(node);
        node.setAttribute('data-deferred-loaded', '');
        window.dispatchEvent(
          new CustomEvent('deferred-collection:loaded', { detail: { target: target, node: node } })
        );
      })
      .catch(function (err) {
        // Leave the heading in place and surface a link rather than a blank gap.
        console.warn('[deferred-collection]', err);
        placeholder.setAttribute('data-deferred-failed', '');
        var slot = placeholder.querySelector('.deferred-collection__slot');
        if (slot) {
          slot.style.minHeight = '0px';
          slot.removeAttribute('aria-busy');
          slot.innerHTML = '';
        }
      });
  }

  function start() {
    var placeholders = document.querySelectorAll('[data-deferred-collection]');
    if (!placeholders.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < placeholders.length; i++) load(placeholders[i]);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          load(entry.target);
        });
      },
      { rootMargin: ROOT_MARGIN }
    );

    for (var k = 0; k < placeholders.length; k++) io.observe(placeholders[k]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
